/**
 * API naming and versioning conventions (§21.1).
 * These constants are the single source of truth for route building and
 * client configuration across apps/api and the Vue front-ends.
 */

export const API_VERSION = "v1" as const;

/** Root prefixes — use these when constructing route paths or base URLs. */
export const API_ROOTS = {
  /** Merchant, internal admin, health, and operational APIs. */
  APP: `/v1/api`,
  /** Shopify session bootstrap and webhooks. */
  SHOPIFY: `/v1/shopify`,
  /** Shopify app-proxy storefront widget APIs. */
  WIDGET: `/v1/widget`,
} as const;

/** Standard HTTP headers required by the API contract. */
export const API_HEADERS = {
  /** Propagated correlation identifier — generated if absent. */
  CORRELATION_ID: "X-Correlation-Id",
  /** Required for documented state-changing routes. */
  IDEMPOTENCY_KEY: "Idempotency-Key",
} as const;

/**
 * Additive changes — permitted within v1 without a version bump.
 * Producers and consumers should be designed to tolerate these.
 */
export const ADDITIVE_API_CHANGES = [
  "Add new optional request fields",
  "Add new optional response fields",
  "Add new endpoints under /v1",
  "Add new enum values to non-exhaustive fields",
  "Add new error codes to existing families",
] as const;

/**
 * Breaking changes — require a new major version (/v2) before deployment.
 * Changing any of these within v1 without a version bump violates the contract.
 */
export const BREAKING_API_CHANGES = [
  "Remove or rename required request or response fields",
  "Change the type of an existing field",
  "Add required fields to existing request bodies",
  "Remove existing endpoints",
  "Change HTTP method or path of an existing operation",
  "Remove existing enum values from exhaustive fields",
] as const;

export type AdditiveApiChange = (typeof ADDITIVE_API_CHANGES)[number];
export type BreakingApiChange = (typeof BREAKING_API_CHANGES)[number];
